import React, { useEffect, useState } from "react";
import { getBookings } from "../../../services/scheduleService";
import { getTimeOffRequests } from "../../../services/timeOffService";
import { fmtHours } from "../timesheets/timesheetsConfig";
import { bookingWarnings, normalizeBooking, normalizeLeave, workingDays } from "./scheduleConfig";
import styles from "./ScheduleUI.module.css";

const hoursBetween = (from, to) => {
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  return Math.max(0, th - fh + (tm - fm) / 60);
};

// Under the booking form's dates and hours: working days and total, plus what the booking would clash with.
export default function BookingChecks({ form, canSeeLeave }) {
  const { employeeId, resourceId, bookingId, startDate: start, endDate: end } = form;
  const perDay = form.isTimeSpecific ? hoursBetween(form.startTime, form.endTime) : Number(form.hoursPerDay) || 0;
  const [context, setContext] = useState({ bookings: [], leave: [] });

  useEffect(() => {
    if (!start || !end || end < start || (!employeeId && !resourceId)) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const [bookings, leave] = await Promise.all([
          getBookings(start, end),
          canSeeLeave && employeeId ? getTimeOffRequests({ start_date: start, end_date: end, status: "approved" }) : [],
        ]);
        if (!cancelled) setContext({ bookings: bookings.map(normalizeBooking), leave: leave.map(normalizeLeave) });
      } catch {
        if (!cancelled) setContext({ bookings: [], leave: [] });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [employeeId, resourceId, start, end, canSeeLeave]);

  if (!start || !end || end < start) return null;
  const days = workingDays(start, end);
  const others = context.bookings.filter((b) => b.id !== Number(bookingId));
  const warnings = bookingWarnings({
    start,
    end,
    perDay,
    others: employeeId ? others.filter((b) => b.employee_id === Number(employeeId)) : [],
    leave: context.leave.filter((l) => l.employee_id === Number(employeeId)),
    resourceOthers: resourceId ? others.filter((b) => b.resource_id === Number(resourceId)) : [],
  });

  return (
    <div className={styles.checks} aria-live="polite">
      <div className={styles.checksTotal}>
        <span>{days} working day{days === 1 ? "" : "s"} × {fmtHours(perDay)}</span>
        <strong>{fmtHours(days * perDay)}</strong>
      </div>
      {warnings.map((w) => <div key={w} className={styles.checkWarn}>{w}</div>)}
    </div>
  );
}
